import axios from 'axios';
import moment from 'moment';

import { BackupJobDto, BackupJobStatistics } from '../types/backupJob';
import { BackupJob } from '../types/settings';
import { getAuthHeader } from '../utils/authUtils';


// API yanıt tipi
export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

// Yedek tipi
export interface DatabaseBackup {
  id: string;
  databaseId: string;
  backupType: string;
  backupFileName: string;
  backupPath: string;
  fileSizeInMB: number;
  createdAt: string;
  createdBy: string;
}

// Yedekleme sonucu tipi
export interface BackupResult {
  success: boolean;
  message: string;
  backupFileName: string;
  backupPath: string;
  backupType: string;
  createdAt: string;
}

// Yedekleme isteği tipi
export interface BackupRequest {
  databaseId: string;
}

// Geri yükleme isteği tipi
export interface RestoreRequest {
  databaseId: string;
  backupId: string;
}

/**
 * Veritabanı yedekleme işlemleri için API servisi
 * Bu servis sadece yedekleme işlemleri için kullanılır, SQL sorguları için değil.
 */
export class DatabaseBackupService {
  /**
   * GUID formatının geçerli olup olmadığını kontrol eder
   * @param guid Kontrol edilecek GUID string değeri
   * @returns GUID formatı geçerli ise true, değilse false
   */
  private isValidGuid(guid: string): boolean {
    if (!guid) return false;
    
    // GUID regex pattern: 8-4-4-4-12 formatında hexadecimal karakterler
    const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return guidRegex.test(guid);
  }
  private apiUrl: string;

  constructor() {
    this.apiUrl = process.env.REACT_APP_API_URL || '';
  }

  /**
   * Veritabanı yedeklerini listeler
   * @param databaseId Veritabanı ID
   * @returns Yedek listesi
   */
  async getBackups(databaseId: string): Promise<ApiResponse<DatabaseBackup[]>> {
    try {
      const response = await axios.get<ApiResponse<DatabaseBackup[]>>(
        `${this.apiUrl}/api/v1/DatabaseBackup/${databaseId}`,
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Yedekler alınırken hata oluştu:', error);
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || 'Yedekler alınırken hata oluştu',
        data: []
      };
    }
  }

  /**
   * Tam yedekleme işlemi yapar
   * @param databaseId Veritabanı ID
   * @returns Yedekleme sonucu
   */
  async createFullBackup(databaseId: string): Promise<ApiResponse<BackupResult>> {
    try {
      // GUID formatı kontrolü
      if (!this.isValidGuid(databaseId)) {
        console.error('Geçersiz GUID formatı:', databaseId);
        return {
          success: false,
          message: 'Geçersiz veritabanı ID formatı. Lütfen geçerli bir GUID değeri gönderin.',
          data: {} as BackupResult
        };
      }
      
      const response = await axios.post<ApiResponse<BackupResult>>(
        `${this.apiUrl}/api/v1/DatabaseBackup/full`,
        { databaseId },
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Tam yedekleme yapılırken hata oluştu:', error);
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || 'Tam yedekleme yapılırken hata oluştu',
        data: {} as BackupResult
      };
    }
  }

  /**
   * Fark yedekleme işlemi yapar
   * @param databaseId Veritabanı ID
   * @returns Yedekleme sonucu
   */
  async createDifferentialBackup(databaseId: string): Promise<ApiResponse<BackupResult>> {
    try {
      // GUID formatı kontrolü
      if (!this.isValidGuid(databaseId)) {
        console.error('Geçersiz GUID formatı:', databaseId);
        return {
          success: false,
          message: 'Geçersiz veritabanı ID formatı. Lütfen geçerli bir GUID değeri gönderin.',
          data: {} as BackupResult
        };
      }
      
      const response = await axios.post<ApiResponse<BackupResult>>(
        `${this.apiUrl}/api/v1/DatabaseBackup/differential`,
        { databaseId },
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Fark yedekleme yapılırken hata oluştu:', error);
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || 'Fark yedekleme yapılırken hata oluştu',
        data: {} as BackupResult
      };
    }
  }

  /**
   * Yedekten geri yükleme işlemi yapar
   * @param databaseId Veritabanı ID
   * @param backupId Yedek ID
   * @returns İşlem sonucu
   */
  async restoreBackup(databaseId: string, backupId: string): Promise<ApiResponse<boolean>> {
    try {
      const response = await axios.post<ApiResponse<boolean>>(
        `${this.apiUrl}/api/v1/DatabaseBackup/restore`,
        { databaseId, backupId },
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Yedekten geri yükleme yapılırken hata oluştu:', error);
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || 'Yedekten geri yükleme yapılırken hata oluştu',
        data: false
      };
    }
  }

  /**
   * Yedekleme istatistiklerini getirir
   * @param databaseId Veritabanı ID
   * @returns Yedekleme istatistikleri
   */
  async getBackupStatistics(databaseId: string): Promise<ApiResponse<BackupJobStatistics>> {
    try {
      const response = await axios.get<ApiResponse<BackupJobStatistics>>(
        `${this.apiUrl}/api/v1/DatabaseBackup/statistics/${databaseId}`,
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Yedekleme istatistikleri alınırken hata oluştu:', error);
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || 'Yedekleme istatistikleri alınırken hata oluştu',
        data: {
          totalJobs: 0,
          activeJobs: 0,
          completedJobs: 0,
          failedJobs: 0,
          lastRun: undefined,
          nextRun: undefined,
          averageDuration: 0,
          totalBackupSize: 0
        }
      };
    }
  }
  
  /**
   * Yedek indirme URL'sini oluşturur
   * @param backupId Yedek ID
   * @returns İndirme URL'si
   */
  getDownloadUrl(backupId: string): string {
    return `${this.apiUrl}/api/v1/DatabaseBackup/download/${backupId}`;
  }

  /**
   * Zamanlanmış yedekleme görevlerini listeler
   * @returns Yedekleme görevleri listesi
   */
  async getScheduledBackupJobs(): Promise<ApiResponse<BackupJobDto[]>> {
    try {
      const response = await axios.get<ApiResponse<BackupJobDto[]>>(
        `${this.apiUrl}/api/v1/backup-jobs`,
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Zamanlanmış yedekleme görevleri alınırken hata oluştu:', error);
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || 'Zamanlanmış yedekleme görevleri alınırken hata oluştu',
        data: []
      };
    }
  }

  /**
   * Belirli bir zamanlanmış yedekleme görevini getirir
   * @param jobId Görev ID
   * @returns Yedekleme görevi
   */
  async getBackupJob(jobId: number): Promise<ApiResponse<BackupJobDto>> {
    try {
      const response = await axios.get<ApiResponse<BackupJobDto>>(
        `${this.apiUrl}/api/v1/backup-jobs/${jobId}`,
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error(`Yedekleme görevi (ID: ${jobId}) alınırken hata oluştu:`, error);
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || `Yedekleme görevi (ID: ${jobId}) alınırken hata oluştu`,
        data: {} as BackupJobDto
      };
    }
  }

  /**
   * Yeni bir zamanlanmış yedekleme görevi oluşturur
   * @param backupJob Oluşturulacak yedekleme görevi
   * @returns Oluşturulan yedekleme görevi
   */
  async createBackupJob(backupJob: BackupJobDto): Promise<ApiResponse<BackupJobDto>> {
    try {
      const response = await axios.post<ApiResponse<BackupJobDto>>(
        `${this.apiUrl}/api/v1/backup-jobs`,
        backupJob,
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Yedekleme görevi oluşturulurken hata oluştu:', error);
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || 'Yedekleme görevi oluşturulurken hata oluştu',
        data: {} as BackupJobDto
      };
    }
  }

  /**
   * Var olan bir zamanlanmış yedekleme görevini günceller
   * @param jobId Görev ID
   * @param backupJob Güncellenecek yedekleme görevi
   * @returns Güncellenen yedekleme görevi
   */
  async updateBackupJob(jobId: number, backupJob: BackupJobDto): Promise<ApiResponse<BackupJobDto>> {
    try {
      const response = await axios.put<ApiResponse<BackupJobDto>>(
        `${this.apiUrl}/api/v1/backup-jobs/${jobId}`,
        backupJob,
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error(`Yedekleme görevi (ID: ${jobId}) güncellenirken hata oluştu:`, error);
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || `Yedekleme görevi (ID: ${jobId}) güncellenirken hata oluştu`,
        data: {} as BackupJobDto
      };
    }
  }

  /**
   * Bir zamanlanmış yedekleme görevini siler
   * @param jobId Silinecek görev ID
   * @returns İşlem sonucu
   */
  async deleteBackupJob(jobId: number): Promise<ApiResponse<boolean>> {
    try {
      const response = await axios.delete<ApiResponse<boolean>>(
        `${this.apiUrl}/api/v1/backup-jobs/${jobId}`,
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error(`Yedekleme görevi (ID: ${jobId}) silinirken hata oluştu:`, error);
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || `Yedekleme görevi (ID: ${jobId}) silinirken hata oluştu`,
        data: false
      };
    }
  }

  /**
   * Bir zamanlanmış yedekleme görevini etkinleştirir veya devre dışı bırakır
   * @param jobId Görev ID
   * @param isEnabled Etkinleştirme durumu
   * @returns İşlem sonucu
   */
  async toggleBackupJobStatus(jobId: number, isEnabled: boolean): Promise<ApiResponse<boolean>> {
    try {
      const response = await axios.put<ApiResponse<boolean>>(
        `${this.apiUrl}/api/v1/backup-jobs/${jobId}/toggle`,
        { isEnabled },
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error: any) {
      const action = isEnabled ? 'etkinleştirilirken' : 'devre dışı bırakılırken';
      console.error(`Yedekleme görevi (ID: ${jobId}) ${action} hata oluştu:`, error);
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || `Yedekleme görevi (ID: ${jobId}) ${action} hata oluştu`,
        data: false
      };
    }
  }

  /**
   * Bir zamanlanmış yedekleme görevini hemen çalıştırır
   * @param jobId Çalıştırılacak görev ID
   * @returns İşlem sonucu
   */
  async runBackupJobNow(jobId: number): Promise<ApiResponse<BackupResult>> {
    try {
      const response = await axios.post<ApiResponse<BackupResult>>(
        `${this.apiUrl}/api/v1/backup-jobs/${jobId}/run-now`,
        {},
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error(`Yedekleme görevi (ID: ${jobId}) çalıştırılırken hata oluştu:`, error);
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || `Yedekleme görevi (ID: ${jobId}) çalıştırılırken hata oluştu`,
        data: {} as BackupResult
      };
    }
  }

  /**
   * Yedekleme görevlerini backend'e job olarak kaydeder (Eski metot - geriye uyumluluk için)
   * @param jobs Kaydedilecek yedekleme görevleri
   * @returns Kayıt sonucu
   * @deprecated Yeni createBackupJob ve updateBackupJob metotlarını kullanın
   */
  async registerBackupJobs(jobs: BackupJob[]): Promise<ApiResponse<any>> {
    try {
      const response = await axios.post<ApiResponse<any>>(
        `${this.apiUrl}/api/v1/DatabaseBackup/register-jobs`,
        { jobs },
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Yedekleme görevleri kaydedilirken hata oluştu:', error);
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || 'Yedekleme görevleri kaydedilirken hata oluştu',
        data: null
      };
    }
  }
}

// Servisin bir örneğini dışa aktar (singleton)
export default new DatabaseBackupService();